import { SESSION_URL, SESSION_REFRESH_URL } from "../constants";

const EXPIRY_KEY = "funnelSessionExpiresAt";
const REFRESH_SKEW_MS = 60_000;

interface FunnelSessionResponse {
  expiresInSeconds: number;
}

let inflight: Promise<void> | null = null;

const isFresh = (): boolean => {
  const expiresAt = Number(sessionStorage.getItem(EXPIRY_KEY));
  return (
    Number.isFinite(expiresAt) &&
    expiresAt > 0 &&
    Date.now() < expiresAt - REFRESH_SKEW_MS
  );
};

const store = (data: FunnelSessionResponse): void => {
  sessionStorage.setItem(
    EXPIRY_KEY,
    String(Date.now() + data.expiresInSeconds * 1000)
  );
};

const tryRefresh = async (): Promise<boolean> => {
  try {
    const response = await fetch(SESSION_REFRESH_URL, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      return false;
    }
    store(await response.json());
    return true;
  } catch {
    return false;
  }
};

const requestSession = async (): Promise<void> => {
  const response = await fetch(SESSION_URL, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Funnel session request failed: ${response.status}`);
  }
  store(await response.json());
};

// Refresh preserves the server-side session id (and thus lead ownership); a brand-new session would not.
const obtainSession = async (hadSession: boolean): Promise<void> => {
  if (hadSession && (await tryRefresh())) {
    return;
  }
  await requestSession();
};

export const ensureFunnelSession = async (): Promise<void> => {
  if (isFresh()) {
    return;
  }

  if (!inflight) {
    const hadSession = sessionStorage.getItem(EXPIRY_KEY) !== null;
    inflight = obtainSession(hadSession).finally(() => {
      inflight = null;
    });
  }

  return inflight;
};
